import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Info } from 'lucide-react';
import { formatNumber } from '../utils/formatters';
import { TOP_50_EXCHANGES } from '../data/top50ExchangesData';
import exchangesDataJSON from '../data/top50ExchangesData.json';

interface Exchange {
  id: string;
  name: string;
  trust_score: number;
  trust_score_rank: number;
  trade_volume_24h_btc: number;
  trade_volume_24h_btc_normalized?: number;
  year_established: number | null;
  country: string | null;
  image: string;
  url?: string;
  has_trading_incentive?: boolean;
  description?: string;
}

const ExchangesPage: React.FC = () => {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'rank' | 'volume' | 'trust'>('rank');
  const itemsPerPage = 20;

  useEffect(() => {
    // Use local data instead of API call
    const loadExchanges = () => {
      try {
        // Merge data from both sources to get all fields
        const mergedExchanges = exchangesDataJSON.map((jsonExchange: any) => {
          const tsExchange = TOP_50_EXCHANGES.find(ex => ex.id === jsonExchange.id);
          return {
            ...jsonExchange,
            ...tsExchange,
            // Ensure we have the normalized volume from JSON data
            trade_volume_24h_btc_normalized: jsonExchange.trade_volume_24h_btc_normalized || jsonExchange.trade_volume_24h_btc,
            // Add default values for missing fields
            has_trading_incentive: false,
          };
        });
        setExchanges(mergedExchanges);
        setLoading(false);
      } catch (error) {
        console.error('Error loading exchanges:', error);
        setLoading(false);
      }
    };

    loadExchanges();
  }, []);

  const getExchangeLogo = (exchange: Exchange) => {
    // Use the image path from the exchange data directly
    // The data already has the correct path format
    return exchange.image;
  };

  const sortedExchanges = [...exchanges].sort((a, b) => {
    switch (sortBy) {
      case 'volume':
        return b.trade_volume_24h_btc - a.trade_volume_24h_btc;
      case 'trust':
        return b.trust_score - a.trust_score;
      default:
        return a.trust_score_rank - b.trust_score_rank;
    }
  });

  const paginatedExchanges = sortedExchanges.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(exchanges.length / itemsPerPage);

  const getTrustScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-400';
    if (score >= 5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getTrustScoreBg = (score: number) => {
    if (score >= 8) return 'bg-green-400/10';
    if (score >= 5) return 'bg-yellow-400/10';
    return 'bg-red-400/10';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-zinc-400">Loading exchanges...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          Cryptocurrency Exchanges
        </h1>
        <p className="text-zinc-400">
          Top cryptocurrency exchanges ranked by trust score and trading volume
        </p>
      </motion.div>

      {/* Stats Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-500 text-sm mb-1">Total Exchanges</p>
          <p className="text-2xl font-bold text-white">{exchanges.length}</p>
        </div>
        <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-500 text-sm mb-1">24h Volume (BTC)</p>
          <p className="text-2xl font-bold text-white">
            {formatNumber(exchanges.reduce((sum, ex) => sum + ex.trade_volume_24h_btc, 0))}
          </p>
        </div>
        <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-500 text-sm mb-1">Avg Trust Score</p>
          <p className="text-2xl font-bold text-white">
            {(exchanges.reduce((sum, ex) => sum + ex.trust_score, 0) / exchanges.length).toFixed(1)}
          </p>
        </div>
        <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
          <p className="text-zinc-500 text-sm mb-1">Countries</p>
          <p className="text-2xl font-bold text-white">
            {new Set(exchanges.map(ex => ex.country).filter(Boolean)).size}
          </p>
        </div>
      </motion.div>

      {/* Sort Options */}
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        <div className="flex items-center gap-2">
          <span className="text-zinc-400 text-sm">Sort by:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setSortBy('rank')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                sortBy === 'rank' 
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
              }`}
            >
              Trust Rank
            </button>
            <button
              onClick={() => setSortBy('volume')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                sortBy === 'volume' 
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
              }`}
            >
              Volume
            </button>
            <button
              onClick={() => setSortBy('trust')}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                sortBy === 'trust' 
                  ? 'bg-violet-500/20 text-violet-400 border border-violet-500/50' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
              }`}
            >
              Trust Score
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-zinc-800">
            <tr>
              <th className="text-left px-6 py-4 text-zinc-400 font-medium">#</th>
              <th className="text-left px-6 py-4 text-zinc-400 font-medium">Exchange</th>
              <th className="text-right px-6 py-4 text-zinc-400 font-medium">Trust Score</th>
              <th className="text-right px-6 py-4 text-zinc-400 font-medium">24h Volume (BTC)</th>
              <th className="text-right px-6 py-4 text-zinc-400 font-medium">Normalized Volume</th>
              <th className="text-center px-6 py-4 text-zinc-400 font-medium">Year Est.</th>
              <th className="text-center px-6 py-4 text-zinc-400 font-medium">Country</th>
              <th className="text-center px-6 py-4"></th>
            </tr>
          </thead>
          <tbody>
            {paginatedExchanges.map((exchange, index) => (
              <motion.tr
                key={exchange.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.02 }}
                className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors"
              >
                <td className="px-6 py-4 text-zinc-400">
                  {exchange.trust_score_rank}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={getExchangeLogo(exchange)}
                      alt={exchange.name}
                      className="w-8 h-8 rounded-full"
                      onError={(e) => {
                        // Fallback to a placeholder image
                        e.currentTarget.src = '/logo192.png';
                      }}
                    />
                    <div>
                      <p className="text-white font-medium">{exchange.name}</p>
                      {exchange.has_trading_incentive && (
                        <span className="text-xs text-yellow-400">Trading Incentive</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className={`inline-flex items-center justify-center px-2 py-1 rounded-lg text-sm font-medium ${getTrustScoreBg(exchange.trust_score)} ${getTrustScoreColor(exchange.trust_score)}`}>
                    {exchange.trust_score}/10
                  </span>
                </td>
                <td className="px-6 py-4 text-right text-white">
                  {formatNumber(exchange.trade_volume_24h_btc)}
                </td>
                <td className="px-6 py-4 text-right text-zinc-400">
                  {formatNumber(exchange.trade_volume_24h_btc_normalized || exchange.trade_volume_24h_btc)}
                </td>
                <td className="px-6 py-4 text-center text-zinc-400">
                  {exchange.year_established || '-'}
                </td>
                <td className="px-6 py-4 text-center text-zinc-400">
                  {exchange.country || '-'}
                </td>
                <td className="px-6 py-4 text-center">
                  {exchange.url && (
                    <a
                      href={exchange.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-violet-400 hover:text-violet-300 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {paginatedExchanges.map((exchange, index) => (
          <motion.div
            key={exchange.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.02 }}
            className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-4"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="text-zinc-500 text-sm">#{exchange.trust_score_rank}</span>
                <img
                  src={getExchangeLogo(exchange)}
                  alt={exchange.name}
                  className="w-10 h-10 rounded-full"
                  onError={(e) => {
                    // Fallback to a placeholder image
                    e.currentTarget.src = '/logo192.png';
                  }}
                />
                <div>
                  <h3 className="text-white font-medium">{exchange.name}</h3>
                  {exchange.has_trading_incentive && (
                    <span className="text-xs text-yellow-400">Trading Incentive</span>
                  )}
                </div>
              </div>
              {exchange.url && (
                <a
                  href={exchange.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-violet-400 hover:text-violet-300 transition-colors"
                >
                  <ExternalLink className="w-5 h-5" />
                </a>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-zinc-500 mb-1">Trust Score</p>
                <span className={`inline-flex items-center justify-center px-2 py-1 rounded-lg text-sm font-medium ${getTrustScoreBg(exchange.trust_score)} ${getTrustScoreColor(exchange.trust_score)}`}>
                  {exchange.trust_score}/10
                </span>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">24h Volume</p>
                <p className="text-white font-medium">{formatNumber(exchange.trade_volume_24h_btc)} BTC</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Established</p>
                <p className="text-white">{exchange.year_established || '-'}</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Country</p>
                <p className="text-white">{exchange.country || '-'}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          <span className="px-4 py-2 text-zinc-400">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-700 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Info Note */}
      <div className="mt-8 flex items-start gap-3 bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
        <Info className="w-5 h-5 text-violet-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-zinc-400">
          <p>Trust Score is calculated based on cybersecurity score, reported volume, liquidity, scale of operations, and more.</p>
          <p className="mt-1">Normalized volume represents the adjusted trading volume after removing wash trading and fake volume.</p>
        </div>
      </div>
    </div>
  );
};

export default ExchangesPage;